import React, { useEffect, useRef } from "react";
import { useOptionalChessClock } from "@react-chess-tools/react-chess-clock";
import type { TimeControlConfig } from "@react-chess-tools/react-chess-clock";
import { Chess, Color, type Move } from "chess.js";

import type {
  ChessGameClockTimeoutEvent,
  ChessGameEvent,
  ChessGameIllegalMoveEvent,
  ChessGameMoveEvent,
} from "../types/gameEvents";
import { cloneGame, getCurrentFen, getGameInfo } from "../utils/chess";

export type useChessGameProps = {
  fen?: string;
  orientation?: Color;
  /** Optional clock configuration to enable chess clock functionality */
  timeControl?: TimeControlConfig;
  /** Automatically switch the clock after each move (default: true).
   * Set to false to let players manually press the clock, mimicking real-life over-the-board play. */
  autoSwitchOnMove?: boolean;
};

export const useChessGame = ({
  fen,
  orientation: initialOrientation,
  timeControl,
  autoSwitchOnMove = true,
}: useChessGameProps = {}) => {
  // Track initial FEN as state for reactive updates in useMemo
  const [initialFen, setInitialFen] = React.useState(fen);
  // Mount guard to skip the fen effect on first render (game already initialized)
  const isMounted = useRef(false);

  const [game, setGame] = React.useState(() => {
    try {
      return new Chess(fen);
    } catch (e) {
      console.error("Invalid FEN:", fen, e);
      return new Chess();
    }
  });

  const [orientation, setOrientation] = React.useState<Color>(
    initialOrientation ?? "w",
  );
  const [currentMoveIndex, setCurrentMoveIndex] = React.useState(-1);
  const [gameEvent, setGameEvent] = React.useState<ChessGameEvent | null>(null);
  const gameEventIdRef = React.useRef(0);
  // Incremented every time a new position is loaded (fen prop change or
  // setPosition), but not on moves. Consumers can key the board on it to
  // discard in-flight move animations that would replay over the new position
  const [positionId, setPositionId] = React.useState(0);

  // Sync game state when fen prop changes (skip on mount to avoid double initialization)
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    try {
      const newGame = new Chess(fen);
      setInitialFen(fen);
      setGame(newGame);
      setCurrentMoveIndex(-1); // Reset move navigation when position changes externally
      setPositionId((id) => id + 1);
    } catch (e) {
      console.error("Invalid FEN:", fen, e);
      setInitialFen(undefined);
      setGame(new Chess());
      setCurrentMoveIndex(-1);
      setPositionId((id) => id + 1);
    }
  }, [fen]);

  // Sync orientation when prop changes
  useEffect(() => {
    if (initialOrientation !== undefined) {
      setOrientation(initialOrientation);
    }
  }, [initialOrientation]);

  const history = React.useMemo(() => game.history(), [game]);
  const isLatestMove =
    currentMoveIndex === history.length - 1 || currentMoveIndex === -1;

  const info = React.useMemo(
    () => getGameInfo(game, orientation),
    [game, orientation],
  );

  const currentFen = React.useMemo(
    () => getCurrentFen(initialFen, game, currentMoveIndex),
    [initialFen, game, currentMoveIndex],
  );

  const currentPosition = game.history()[currentMoveIndex];

  const clockState = useOptionalChessClock(timeControl);

  // Keep clockState in a ref to avoid re-creating makeMove on every clock tick.
  // The clock state object is recreated on every render (especially during active
  // ticking), which would defeat the useCallback memoization.
  const clockStateRef = useRef(clockState);
  clockStateRef.current = clockState;
  const previousTimeoutRef = React.useRef(clockState?.timeout ?? null);

  const emitGameEvent = React.useCallback(
    (
      event:
        | Omit<ChessGameMoveEvent, "id">
        | Omit<ChessGameIllegalMoveEvent, "id">
        | Omit<ChessGameClockTimeoutEvent, "id">,
    ) => {
      gameEventIdRef.current += 1;
      setGameEvent({
        ...event,
        id: gameEventIdRef.current,
      });
    },
    [],
  );

  useEffect(() => {
    const timeout = clockState?.timeout ?? null;

    if (previousTimeoutRef.current === null && timeout !== null) {
      emitGameEvent({
        type: "clock-timeout",
        player: timeout,
      });
    }

    previousTimeoutRef.current = timeout;
  }, [clockState?.timeout, emitGameEvent]);

  const getGameEventForMove = React.useCallback(
    (nextGame: Chess, moveResult: Move): Omit<ChessGameMoveEvent, "id"> => ({
      type: "move-made",
      move: moveResult,
      fen: nextGame.fen(),
      isCheck: nextGame.isCheck(),
      isCheckmate: nextGame.isCheckmate(),
      isDraw: nextGame.isDraw(),
    }),
    [],
  );

  const setPosition = React.useCallback((fen: string, orientation: Color) => {
    try {
      const newGame = new Chess();
      newGame.load(fen);
      setInitialFen(fen); // Update state when position is set imperatively
      setOrientation(orientation);
      setGame(newGame);
      setCurrentMoveIndex(-1);
      setPositionId((id) => id + 1);
    } catch (e) {
      console.error("Failed to load FEN:", fen, e);
    }
  }, []);

  const makeMove = React.useCallback(
    (move: Parameters<Chess["move"]>[0]): boolean => {
      // Only allow moves when we're at the latest position
      if (!isLatestMove) {
        return false;
      }

      // Access clock state via ref to avoid stale closures while keeping
      // the callback stable (not re-created on every clock tick)
      const clock = clockStateRef.current;

      // Don't allow moves after clock timeout
      if (clock && clock.timeout !== null) {
        return false;
      }

      try {
        const copy = cloneGame(game);
        const moveResult = copy.move(move);
        setGame(copy);
        setCurrentMoveIndex(copy.history().length - 1);
        emitGameEvent(getGameEventForMove(copy, moveResult));

        // Auto-start clock on first move
        if (clock && clock.status === "idle") {
          clock.methods.start();
        }

        // Pause clock on game over (checked immediately after move)
        if (clock && clock.status === "running" && copy.isGameOver()) {
          clock.methods.pause();
        }

        // Auto-switch clock after a move is made if enabled
        if (autoSwitchOnMove && clock && clock.status !== "finished") {
          clock.methods.switch();
        }

        return true;
      } catch (e) {
        emitGameEvent({
          type: "illegal-move",
          attemptedMove: move,
        });
        return false;
      }
    },
    [isLatestMove, game, autoSwitchOnMove, emitGameEvent, getGameEventForMove],
  );

  const flipBoard = React.useCallback(() => {
    setOrientation((orientation) => (orientation === "w" ? "b" : "w"));
  }, []);

  const goToMove = React.useCallback(
    (moveIndex: number) => {
      if (moveIndex < -1 || moveIndex >= history.length) return;
      setCurrentMoveIndex(moveIndex);
    },
    [history.length],
  );

  const goToStart = React.useCallback(() => goToMove(-1), []);
  const goToEnd = React.useCallback(
    () => goToMove(history.length - 1),
    [history.length],
  );
  const goToPreviousMove = React.useCallback(
    () => goToMove(currentMoveIndex - 1),
    [currentMoveIndex],
  );
  const goToNextMove = React.useCallback(
    () => goToMove(currentMoveIndex + 1),
    [currentMoveIndex],
  );

  const methods = React.useMemo(
    () => ({
      makeMove,
      setPosition,
      flipBoard,
      goToMove,
      goToStart,
      goToEnd,
      goToPreviousMove,
      goToNextMove,
    }),
    [
      makeMove,
      setPosition,
      flipBoard,
      goToMove,
      goToStart,
      goToEnd,
      goToPreviousMove,
      goToNextMove,
    ],
  );

  return {
    game,
    currentFen,
    currentPosition,
    orientation,
    currentMoveIndex,
    isLatestMove,
    positionId,
    info,
    gameEvent,
    methods,
    clock: clockState,
  };
};
